import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, LayoutDashboard, FileText, BarChart3, Upload, LogOut, Moon, Sun, Users, Settings, Brain, Code, User, AlertTriangle, DollarSign } from 'lucide-react';
import UniversalSearch from '../UniversalSearch';
import api from '../../utils/api';

function DashboardLayout({ children, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingDuplicatesCount, setPendingDuplicatesCount] = useState(0);

  // Fetch current user info
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setCurrentUser(response.data);
        localStorage.setItem('userRole', response.data.role);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch pending duplicates count
  useEffect(() => {
    const fetchPendingDuplicatesCount = async () => {
      try {
        const response = await api.get('/cases/potential-duplicates');
        setPendingDuplicatesCount(response.data.total || 0);
      } catch (error) {
        console.error('Failed to fetch pending duplicates count:', error);
      }
    };
    
    // Fetch initially
    fetchPendingDuplicatesCount();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingDuplicatesCount, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Detect mobile screen and manage sidebar state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-close sidebar on mobile, open on desktop
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebarOnMobile = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference if no saved theme
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyName, setCompanyName] = useState('GoGet ScratchMatch');
  const location = useLocation();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Fetch company logo and name from config
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/public/branding');
        if (response.data.company_logo) {
          setCompanyLogo(response.data.company_logo);
        }
        if (response.data.company_name) {
          setCompanyName(response.data.company_name);
        }
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Cases', href: '/cases', icon: FileText },
    { name: 'Billing', href: '/billing', icon: DollarSign },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'AI Feedback', href: '/ai-feedback', icon: Brain, adminOnly: true },
    { name: 'Potential Duplicates', href: '/potential-duplicates', icon: AlertTriangle },
    { name: 'Upload', href: '/intake', icon: Upload },
    { name: 'User Management', href: '/users', icon: Users, adminOnly: true },
    { name: 'Configuration', href: '/config', icon: Settings, adminOnly: true },
    { name: 'API Docs', href: '/api-docs', icon: Code, adminOnly: true },
  ];

  // Filter navigation based on user role
  const isAdmin = currentUser?.role === 'admin';
  const filteredNavigation = navigation.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeSidebarOnMobile}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}
        ${sidebarOpen ? 'w-60' : (isMobile ? '-translate-x-full' : 'w-20')}
        bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 
        transition-all duration-300 flex flex-col
        ${isMobile ? 'transform' : ''}
      `}>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          {companyLogo ? (
            <div className={`flex ${sidebarOpen ? 'flex-col items-center gap-2' : 'items-center justify-center'}`}>
              <img 
                src={companyLogo} 
                alt="GoGet"
                className={`${sidebarOpen ? 'h-8' : 'h-6'} w-auto object-contain transition-all`}
              />
              {sidebarOpen && (
                <span className="font-heading font-bold text-orange-600 text-xl">
                  ScratchMatch
                </span>
              )}
            </div>
          ) : (
            <div className={`flex ${sidebarOpen ? 'flex-col items-center gap-2' : 'items-center justify-center'}`}>
              <h1 className={`font-heading font-bold text-orange-600 ${sidebarOpen ? 'text-2xl' : 'text-sm'} transition-all`}>
                {sidebarOpen ? 'GoGet' : 'GG'}
              </h1>
              {sidebarOpen && (
                <span className="font-heading font-bold text-orange-600 text-xl">
                  ScratchMatch
                </span>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            const isPotentialDuplicates = item.href === '/potential-duplicates';
            const showBadge = isPotentialDuplicates && pendingDuplicatesCount > 0;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={closeSidebarOnMobile}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors relative ${
                  isActive
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="font-medium flex-1">{item.name}</span>
                )}
                {showBadge && (
                  <span className={`
                    ${sidebarOpen 
                      ? 'px-2 py-0.5 text-xs' 
                      : 'absolute -top-1 -right-1 w-5 h-5 text-[10px] flex items-center justify-center'
                    }
                    bg-red-500 text-white rounded-full font-semibold
                  `}>
                    {pendingDuplicatesCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {darkMode ? <Sun className="w-5 h-5 flex-shrink-0" /> : <Moon className="w-5 h-5 flex-shrink-0" />}
            {sidebarOpen && <span className="font-medium">Theme</span>}
          </button>
          <Link
            to="/profile"
            onClick={() => isMobile && setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <User className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Profile</span>}
          </Link>
          <button
            onClick={onLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-slate-600 dark:text-slate-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        {/* Mobile Top Bar with Hamburger */}
        <div className="md:hidden bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-6 h-6 text-slate-700 dark:text-slate-300" />
          </button>
          <h1 className="text-lg font-heading font-bold text-orange-600 dark:text-orange-500">
            {companyName}
          </h1>
          <UniversalSearch />
        </div>

        {/* Desktop Sidebar Toggle - Positioned in top bar area */}
        {!isMobile && (
          <div className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </button>
            <UniversalSearch />
          </div>
        )}

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;
